import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { chatAPI, patientAPI } from '../services/api';

const ChatInterface = () => {
  const { patientId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [patient, setPatient] = useState(null);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    fetchPatient();
    fetchChatHistory();
  }, [patientId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchPatient = async () => {
    try {
      const response = await patientAPI.getById(patientId);
      setPatient(response.data);
    } catch (err) {
      console.error('Error fetching patient:', err);
    }
  };

  const fetchChatHistory = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getHistory(patientId);
      setMessages(response.data);
    } catch (err) {
      setError('Failed to load chat history');
      console.error('Error fetching chat history:', err);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const userMessage = newMessage.trim();
    setNewMessage('');
    setSending(true);
    setError('');

    try {
      await chatAPI.sendMessage(patientId, userMessage);
      await fetchChatHistory();
    } catch (err) {
      setError('Failed to send message');
      console.error('Error sending message:', err);
      setNewMessage(userMessage);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading && messages.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 text-lg">Loading chat history...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] max-w-4xl mx-auto bg-white rounded-lg shadow-md border border-gray-200">
      {/* Chat Header */}
      <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <h2 className="text-lg font-semibold text-gray-800">
          Chat with {patient?.name || 'Patient'}
        </h2>
        <button 
          className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          onClick={() => window.history.back()}
        >
          ← Back to Patients
        </button>
      </div>

      {/* Chat Messages Area */}
      <div 
        className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50"
        ref={chatContainerRef}
      >
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p>No messages yet. Start a conversation!</p>
          </div>
        ) : (
          messages.map((msg, index) => (
            <div key={index} className="space-y-4">
              {/* User Message */}
              <div className="flex justify-end">
                <div className="max-w-[70%] bg-blue-600 text-white rounded-lg rounded-br-none px-4 py-2 shadow-sm">
                  <div className="text-sm">
                    <span className="font-medium">You:</span> {msg.user_message}
                  </div>
                  <div className="text-xs text-blue-100 text-right mt-1">
                    {formatTime(msg.created_at)}
                  </div>
                </div>
              </div>
              
              {/* Assistant Message */}
              <div className="flex justify-start">
                <div className="max-w-[70%] bg-white border border-gray-200 rounded-lg rounded-bl-none px-4 py-2 shadow-sm">
                  <div className="text-sm text-gray-800">
                    <span className="font-medium text-gray-600">Assistant:</span> {msg.ai_response}
                  </div>
                  <div className="text-xs text-gray-500 text-right mt-1">
                    {formatTime(msg.created_at)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Chat Input Form */}
      <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4 bg-white rounded-b-lg">
        <div className="flex gap-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            disabled={sending}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending
              </span>
            ) : (
              'Send'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;